import type { ChangeDetails } from "../reviewWorkflow";

/**
 * Line fields owned by `handleValidationComplete` / api_*_validation
 * (GST mapping, TDS classification, derived line totals).
 * Shown in review UIs only when present in explicit `field_changes` (user edit).
 */
export const VALIDATION_OWNED_LINE_FIELD_KEYS = new Set([
  "tax_name",
  "tax_percentage",
  "tax_amount",
  "tds_tax_name",
  "tds_tax_percentage",
  "item_total",
  "item_total_inclusive_of_tax",
]);

type ValidationSnapshot = NonNullable<ChangeDetails["validation_snapshot"]>;

/**
 * Build the post-validation baseline attached to approve / review ChangeDetails.
 * Line items come from the locked validation baseline; header totals from the
 * document after validation so GST Total / Grand Total system recalcs are hidden.
 */
export function buildValidationSnapshotForReview(
  baselineLines: unknown[] | null | undefined,
  header?: {
    tax_total?: unknown;
    sub_total?: unknown;
    total?: unknown;
  } | null,
): ValidationSnapshot | undefined {
  if (!Array.isArray(baselineLines) || baselineLines.length === 0) {
    return undefined;
  }

  const snapshot: ValidationSnapshot = {
    line_items: baselineLines,
  };

  if (header) {
    const taxTotal = toOptionalNumber(header.tax_total);
    if (taxTotal !== undefined) snapshot.tax_total = taxTotal;
    const subTotal = toOptionalNumber(header.sub_total);
    if (subTotal !== undefined) snapshot.sub_total = subTotal;
    const total = toOptionalNumber(header.total);
    if (total !== undefined) snapshot.total = total;
  }

  return snapshot;
}

function toOptionalNumber(value: unknown): number | undefined {
  if (value === undefined || value === null || value === "") return undefined;
  const n = typeof value === "number" ? value : parseFloat(String(value));
  return Number.isFinite(n) ? n : undefined;
}
